import { isClient } from '@vueuse/core'
import { getFirstDomain, getPrjId } from '~/utils'
import { getOrCreateClientId } from '@jctrans-materials/shared'

const TokenKey = 'JC-JAVA-Token-Root'
const ExpiresInKey = 'JC-Expires-In'
const ExpiresInTimeKey = 'JC-Expires-Time-In'
const RefreshTokenKey = 'JC-Refresh-Token'

async function setClientId() {
  if (isClient) {
    const app = useNuxtApp()

    localStorage.setItem('clientId', await app.$getFingerprint())
  }
}

function useRootCookie(key: string, opts: any = {}) {
  const domain = getFirstDomain()
  return useCookie(key, {
    domain,
    ...opts,
  })
}

// 获取 token
function getToken() {
  return useRootCookie(TokenKey).value
}

// 工程默认APPID获取
function getAppId() {
  return 'MPERA'
}

// 设置 token
function setToken(token: any) {
  return (useRootCookie(TokenKey, { maxAge: 60 * 60 * 24 * 365 }).value = token)
}

// 移除 token
function removeToken() {
  useRootCookie(RefreshTokenKey).value = null
  useRootCookie(TokenKey).value = null
}

/** 仅清理本地登录态，不跳转登录页 */
function clearLocalAuthState() {
  const systemStore = useMySystemStore()
  const userStore = useMyUserStore()
  systemStore.token = ''
  removeToken()
  removeExpiresIn()
  userStore.resetUserCompInfo()
}

// 获取过期时间
function getExpiresIn() {
  return useRootCookie(ExpiresInKey).value || -1
}

// 设置过期时间
function setExpiresIn(time: any) {
  return (useRootCookie(ExpiresInKey, { maxAge: 60 * 60 * 24 * 365 }).value =
    time)
}

// 移除过期时间
function removeExpiresIn() {
  useRootCookie(ExpiresInTimeKey).value = null
  useRootCookie(ExpiresInKey).value = null
}

// 设置 refresh token
function setRefreshToken(token: string) {
  return (useRootCookie(RefreshTokenKey, { maxAge: 60 * 60 * 24 * 365 }).value =
    token)
}

// 获取 refresh token
function getRefreshToken() {
  return useRootCookie(RefreshTokenKey).value || ''
}

// 获取过期时间点
function getExpiresTimeIn() {
  return useRootCookie(ExpiresInTimeKey).value || ''
}

// 设置过期时间点
function setExpiresTimeIn(time: any) {
  return (useRootCookie(ExpiresInTimeKey, {
    maxAge: 60 * 60 * 24 * 365,
  }).value = time)
}

// 统一生成认证域名，优先运行时配置，兜底历史全局变量
function getAuthHost() {
  const config = useRuntimeConfig()
  const global =
    config.public.LOGIN_SITE +
    `${config.public.ENV !== 'prod' ? '-' + config.public.ENV : ''}` +
    '.' +
    config.public.DOMAIN_NAME
  return global || window.BASE_INFO.VUE_APP_LOGIN_PAGE
}

// APP 登录/注册回调地址，追加时间戳避免缓存
function getAppCallbackUrl(fullPath: string) {
  return encodeURIComponent(`${fullPath}?appPageReload=${Date.now()}`)
}

// 仅在存在 prjId 时拼接，保持历史参数行为
function appendPrjId(path: string) {
  const prjId = getPrjId()
  return prjId ? `${path}&prjId=${prjId}` : path
}

// 统一拼接 GIO 跟踪参数，保持 '-' 作为空值哨兵
function appendGioTrackParams(path: string, entry = '-') {
  let nextPath = path

  if (entry !== '-') {
    nextPath += `&GIO_Entry=${entry}`
  }

  const pageCode = useRoute().meta.pageCode || '-'
  if (pageCode !== '-') {
    nextPath += `&GIO_PAGECODE=${pageCode}`
  }

  return nextPath
}

// 统一使用 a 标签 click 触发跳转，兼容既有行为
function redirectWithAnchor(path: string, target = '_self') {
  const link = document.createElement('a')
  link.href = path
  link.target = target

  // 使用 createEvent 创建点击事件
  const event = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
  })

  link.dispatchEvent(event)
}

// 构建登录页 URL（含可选 click_source）
function buildLoginPath(fullPath: string, source = '') {
  const appId = encodeURIComponent(getAppId())
  const encodedFullPath = encodeURIComponent(fullPath)
  const clickSource = source ? `&click_source=${source}` : ''

  return `${getAuthHost()}?appId=${appId}${clickSource}&path=${encodedFullPath}`
}

// 构建注册页 URL（含可选 click_source/registerId）
function buildRegisterPath(fullPath: string, source = '', key?: string) {
  const appId = encodeURIComponent(getAppId())
  const encodedFullPath = encodeURIComponent(fullPath)
  const clickSource = source ? `&click_source=${source}` : ''
  const registerId = key ? `&registerId=${key}` : ''

  return `${getAuthHost()}/register?appId=${appId}${clickSource}${registerId}&path=${encodedFullPath}`
}

async function go2LoginPage(fullPath = '/', entry = '-') {
  const systemStore = useMySystemStore()
  if (systemStore.isAPP) {
    // APP 端走宿主登录流程
    const appUtils = new AppUtils()
    // 注意在 query 里加上 appPageReload，为防缓存可用时间戳
    const callbackUrl = getAppCallbackUrl(fullPath)
    await appUtils.login(callbackUrl, '')
    return
  }

  // H5 端走登录站点，按历史顺序追加参数
  let path = buildLoginPath(fullPath)
  path = appendPrjId(path)
  path = appendGioTrackParams(path, entry)

  redirectWithAnchor(path, '_self')
}

async function go2LoginPageWithCrmSource(
  fullPath = '/',
  source = '',
  entry = '-',
  target = '_self',
) {
  const systemStore = useMySystemStore()

  if (systemStore.isAPP) {
    // APP 端走宿主登录流程（保留 source）
    const appUtils = new AppUtils()
    // 注意在 query 里加上 appPageReload，为防缓存可用时间戳
    const callbackUrl = getAppCallbackUrl(fullPath)
    await appUtils.login(callbackUrl, source)
    return
  }

  // H5 端登录 URL，保留 target 可控打开方式
  let path = buildLoginPath(fullPath, source)
  path = appendPrjId(path)
  path = appendGioTrackParams(path, entry)

  redirectWithAnchor(path, target)
}

type RegisterData = {
  source: string
  key?: string
}

async function go2RegisterPage(
  fullPath = '/',
  registerData: RegisterData = {
    source: '',
  },
  appRegisterData = {},
  entry = '-',
  target = '_self',
) {
  const systemStore = useMySystemStore()
  if (systemStore.isAPP) {
    // APP 端走宿主注册流程
    const appUtils = new AppUtils()
    // 注意在 query 里加上 appPageReload，为防缓存可用时间戳
    const callbackUrl = getAppCallbackUrl(fullPath)
    // await appUtils.login(callbackUrl, '')
    await appUtils.register({
      callbackUrl,
      ...appRegisterData,
    })
    return
  }

  const { source, key } = registerData
  // H5 端注册 URL，按历史顺序追加参数
  let path = buildRegisterPath(fullPath, source, key)
  path = appendPrjId(path)
  path = appendGioTrackParams(path, entry)

  redirectWithAnchor(path, target)
}

/**
 * 跳转至会员介绍
 */
function go2MemberIntroductionPage() {
  navigateTo({
    name: 'membershipJCTransIntroduction',
  })
}

/**
 * 跳转至加入我们
 * 未登录统一跳登录（回跳到当前入口页）；已登录再进 JoinUs 页唤起表单
 */
async function go2JoinUs(
  crmMarker?: string,
  jumpsource?: string,
  markerClickSource = '',
  markerClickSourceFORM = '',
  formEntry = '',
  extraQuery: Record<string, any> = {},
) {
  const route = useRoute()
  const query: any = { ...extraQuery }
  if (crmMarker) {
    query.crmMarker = crmMarker
  }
  if (jumpsource) {
    query.jumpsource = jumpsource
  }
  // 会员申请
  if (markerClickSourceFORM) {
    query.markerClickSourceFORM = markerClickSourceFORM
  }
  // 注册
  if (markerClickSource) {
    query.markerClickSource = markerClickSource
  }
  query.pageCode = route.meta.pageCode as string
  if (formEntry) {
    query.formEntry = formEntry
  }

  const systemStore = useMySystemStore()
  const userStore = useMyUserStore()
  systemStore.initTokenFromCookie()
  const token = getToken() || systemStore.token

  // 无 token：跳登录，带上注册线索 click_source
  if (!token) {
    go2LoginPageWithCrmSource(
      route.fullPath,
      markerClickSource,
      formEntry || '-',
    )
    return
  }

  // 有 token 但尚未拉到 userId（登录刚回跳竞态）：先补齐用户信息
  if (!userStore.userId) {
    try {
      await userStore.getUserCompanyInfo()
    } catch {
      go2LoginPageWithCrmSource(
        route.fullPath,
        markerClickSource,
        formEntry || '-',
      )
      return
    }
  }

  if (!userStore.userId) {
    go2LoginPageWithCrmSource(
      route.fullPath,
      markerClickSource,
      formEntry || '-',
    )
    return
  }

  navigateTo({
    name: 'JoinUs',
    query,
  })
}

const generateClientUid = () => {
  const idState = useState<string | null>('gdp_identify_id')
  const uid = getOrCreateClientId()
  idState.value = uid
  return uid
}

export {
  getToken,
  setToken,
  getExpiresTimeIn,
  setExpiresTimeIn,
  getRefreshToken,
  setRefreshToken,
  getExpiresIn,
  setExpiresIn,
  removeExpiresIn,
  removeToken,
  clearLocalAuthState,
  getAppId,
  setClientId,
  go2LoginPage,
  go2LoginPageWithCrmSource,
  go2RegisterPage,
  go2JoinUs,
  go2MemberIntroductionPage,
  generateClientUid,
  TokenKey,
  RefreshTokenKey,
  ExpiresInTimeKey,
  ExpiresInKey,
}
